/**
 * Contract-term display labels.
 *
 * Terms are stored, keyed, sorted and costed in MONTHS everywhere — `cost_data` JSONB keys
 * ("12", "34_stepped"), `available_terms`, `termMonths`, expiry-date math. None of that
 * changes. This module owns only how a term is WRITTEN for a human.
 *
 *   12 months -> "Year 1"      24 -> "Year 2"      36 -> "Year 3"      48 -> "Year 4"
 *
 * A non-standard term is snapped to its nearest requested term first
 * (getNearestRequestedTerm), so 8 -> 12 -> "Year 1" and 22 -> 24 -> "Year 2". This module
 * is deliberately given the ALREADY-SNAPPED month value; it does not snap.
 *
 * ⚠️ "Year N" is overloaded in this codebase. Inside a single term, `year_1`/`year_2`/... are
 * the RATE ESCALATION years — a 48-month stepped offer has four different peak rates. Both a
 * term and a rate year read "Year N" on screen (formatTermLabel and formatRateYearLabel), so
 * the two are told apart by CONTEXT, not wording. Nothing matches on either string: the
 * proposal generator pairs costs to terms through the hidden __TERM_N__ stamp. Still use the
 * right helper for the right thing — a term's number is snapped, a rate year's is not.
 *
 * Free of `import.meta` / Node globals so both the React app and the Express server can
 * import it (same arrangement as envSchemeRegistry.ts / ratePrecision.ts).
 */

/**
 * The standard terms offers are requested at (sys_config.requested_contract_terms), which are
 * also the buckets a non-standard term is grouped under.
 *
 * Held at module level because labelling has to snap and almost none of the ~30 call sites has
 * the list to hand — OfferCard, OfferComparison, SiteOffersView and the rest pass a raw
 * `offer.termMonths` straight in. Safe as global state: sys_config holds a single row with no
 * org column, so every tender in the process shares one list.
 *
 * This is also the single definition of the standard terms, imported by the config services and the hook
 * that fetch the live value, so a missing or slow fetch cannot label a term differently from a
 * loaded one.
 *
 * Must track sys_config.requested_contract_terms. Both dev and prod hold 12/24/36/48/60/72; a
 * default that stopped at 48 labelled a 54-month term "Year 4" on any screen whose components
 * had not fetched the config — which was every screen but Compare & Select — while the same
 * term read "Year 5" once loaded. Extending the default costs nothing: a bucket nothing maps
 * to renders no page and no dropdown entry, because both are built from the terms the offers
 * actually carry.
 */
export const DEFAULT_TERM_BUCKETS = [12, 24, 36, 48, 60, 72];
let termBuckets: number[] = [...DEFAULT_TERM_BUCKETS];

/** Point labelling at the configured buckets. Ignores an empty list rather than un-bucketing. */
export const setTermBuckets = (terms: number[] | null | undefined): void => {
  if (Array.isArray(terms) && terms.length > 0) {
    termBuckets = [...terms].sort((a, b) => a - b);
  }
};

/** The buckets currently in force. */
export const getTermBuckets = (): number[] => [...termBuckets];

/**
 * Snap an actual term to the nearest bucket. Ties go to the LARGER bucket, so an 18-month
 * offer — exactly between 12 and 24 — is grouped and labelled as Year 2.
 *
 * This is the one definition; offerTermUtils.getNearestRequestedTerm delegates to it so a
 * term cannot be bucketed by one rule and labelled by another.
 */
export const snapToTermBucket = (months: number, buckets: number[] = termBuckets): number => {
  const n = Number(months);
  if (!isFinite(n)) return months;
  if (!Array.isArray(buckets) || buckets.length === 0) return n;

  let nearest = buckets[0];
  let smallest = Math.abs(n - nearest);
  for (const bucket of buckets) {
    const distance = Math.abs(n - bucket);
    if (distance < smallest || (distance === smallest && bucket > nearest)) {
      nearest = bucket;
      smallest = distance;
    }
  }
  return nearest;
};

/**
 * Whole years for a term, for labelling only.
 *
 * Snaps to the nearest bucket FIRST, then converts to years, so the label always names the
 * group the offer was actually placed in. Without the snap, `Math.round(m / 12)` runs past the
 * last bucket — with a list ending at 48, a 54-month term read "Year 5" on its card and
 * "Year 4" in the comparison dropdown and the generated proposal. Below 54 the two agreed,
 * which is why it went unseen.
 *
 * Floors at 1 — a 3-month term is still "Year 1", never "Year 0".
 */
export const yearsForTerm = (months: number): number => {
  const n = Number(months);
  if (!isFinite(n) || n <= 0) return 1;
  return Math.max(1, Math.round(snapToTermBucket(n) / 12));
};

/**
 * "Year 2". Pass the SNAPPED month value (see module note).
 *
 * `mapping` is the reviewer's own term -> year assignment and wins over the derived number.
 * Omit it and the derived number stands, which is what every offer nobody has mapped gets.
 */
export const formatTermLabel = (months: number, mapping?: TermYearMapping | null): string =>
  `Year ${termYearOf(months, mapping)}`;

/**
 * The same term, worded as a contract LENGTH rather than a year index: "3 Year", not "Year 3".
 *
 * ⚠️ SAME NUMBER, DIFFERENT WORD ORDER — AND THAT IS THE WHOLE POINT.
 *
 * The best-offers cards in the generated proposal read "Year 3 Contract", which describes a
 * position in the tender's term series, not a length. Asked for as "1 Year Contract, 2 Year
 * Contract etc." — the template supplies the trailing " Contract", so this supplies "3 Year".
 *
 * It deliberately reuses termYearOf rather than deriving years from the month count. The card
 * has to carry the SAME number as the offer-table page its figures came from: on a 4/16/28/40
 * tender the 16-month offer belongs to Year 2 and its page is headed Year 2, and a card reading
 * "1 Year Contract" (16 ÷ 12, rounded) would put two different numbers on one set of dollars —
 * the exact bug the note on formatTermLabel's card call site records being fixed.
 *
 * So an unconventional 23-month term still reads "2 Year Contract". That is a bucket name, the
 * one the rest of the document already uses for it, not a claim about its exact duration — the
 * card prints the real start and end dates directly beneath.
 */
export const formatContractTermLabel = (months: number, mapping?: TermYearMapping | null): string =>
  `${termYearOf(months, mapping)} Year`;

/**
 * The TENDER's term -> year assignment, held at module level.
 *
 * Every screen after review — Compare & Select, the offer cards, the recommendation, the rate
 * tables — labels terms through ~21 call sites that receive a bare `offer.termMonths` and have
 * no way to reach the assignment. Threading it through all of them is how a label ends up
 * agreeing on one screen and disagreeing on the next, which is the bug this exists to stop.
 *
 * Held per TENDER rather than per offer, and that is the correct scope, not a shortcut:
 * Compare & Select puts every retailer side by side under ONE set of year columns, so the
 * question "which year is the 16-month term" has a single answer for the whole tender. It is
 * also why WPG and GORKHA can disagree — they are different tenders.
 *
 * Same arrangement as termBuckets above, with one deliberate difference: the SERVER must never
 * use this. Buckets are org-wide and identical for every request, but a tender mapping is not —
 * two proposals generating concurrently would read each other's. Server-side callers pass the
 * mapping explicitly (see formatTermLabel's `mapping` argument); this global is browser-only,
 * where the wizard holds exactly one tender at a time.
 */
const termYearsByTender = new Map<string, Map<number, number>>();
let activeTenderId: string | null = null;

/**
 * ⚠️ KEYED BY TENDER, AND NEVER CLEARED TO EMPTY. Both properties are load-bearing, and each
 * one is a bug I shipped:
 *
 *   A single shared slot leaked ACROSS tenders. Tender "wde" (Origin, 4/16/28/40) assigns
 *   {16:2, 28:3, 40:4} and leaves its 4-month term derived, so it must read Years 1/2/3/4. It
 *   rendered Year 5/1/2/3 — exactly what {"4": 5} produces — because another tender holding
 *   that assignment had been opened first.
 *
 *   Clearing the slot when a load STARTED then fixed the leak but created a worse one: the
 *   assignment was empty for every render until the fetch resolved, and permanently if the
 *   fetch took an error path. Re-editing a year and returning to Step 4 showed fully derived
 *   labels — 1/1/2/3 — as though nothing had ever been assigned.
 *
 * Keying by tender makes a foreign assignment unreachable rather than merely unlikely, so
 * nothing has to be cleared and no window exists where the answer is wrong. Correctness stops
 * depending on which component fetched first.
 */
export const setTermYearMapping = (
  mapping?: TermYearMapping | null,
  tenderId?: string | null,
): void => {
  const key = tenderId ?? activeTenderId;
  if (!key) return;                                  // nothing to key it to; refuse silently
  termYearsByTender.set(key, normaliseTermYearMapping(mapping));
  activeTenderId = key;
};

/**
 * Name the tender being rendered. Called wherever the app already knows it, synchronously on
 * navigation, so a label never resolves against a tender the user has left. An unknown tender
 * simply has no assignment yet and derives until its offers load.
 */
export const setActiveTender = (tenderId?: string | null): void => {
  activeTenderId = tenderId ?? null;
};

/** The active tender's assignment, as month-count -> year. Empty when it has none. */
export const getActiveTermYearMapping = (): Map<number, number> =>
  new Map(activeTenderId ? termYearsByTender.get(activeTenderId) ?? [] : []);

/** Forget every stored assignment. Tests only — production never needs to clear. */
export const resetTermYearMappings = (): void => {
  termYearsByTender.clear();
  activeTenderId = null;
};

/**
 * One tender-wide assignment from the offers that carry one.
 *
 * The assignment is stored per offer (that is where the reviewer sets it) but READ per tender,
 * because Compare & Select ranks every retailer under one set of year columns. Merging is what
 * bridges the two.
 *
 * ⚠️ First assignment wins, in the order given. Two offers CAN disagree about which year a
 * 16-month term is — nothing stops a reviewer setting them differently — and there is no
 * correct answer to pick, because the disagreement itself is the error. Taking the first keeps
 * it deterministic instead of depending on fetch order; the review screen's own conflict block
 * is what stops one offer being internally inconsistent.
 */
export const mergeTermYearMappings = (
  mappings: Array<TermYearMapping | null | undefined>,
): TermYearMapping => {
  const merged: Record<string, number> = {};
  for (const mapping of mappings) {
    for (const [months, year] of normaliseTermYearMapping(mapping)) {
      if (merged[String(months)] === undefined) merged[String(months)] = year;
    }
  }
  return merged;
};

/**
 * The year a term is: the explicit assignment if given, else the tender's, else derived.
 *
 * The explicit argument wins so a caller holding a specific offer's data — the review screen,
 * or any server path — is never overridden by whatever the browser last loaded.
 */
export const termYearOf = (months: number, mapping?: TermYearMapping | null): number => {
  const key = typeof months === 'number' ? months : parseInt(String(months), 10);
  if (mapping !== undefined && mapping !== null) {
    const assigned = normaliseTermYearMapping(mapping).get(key);
    if (assigned !== undefined) return assigned;
  }
  return getActiveTermYearMapping().get(key) ?? yearsForTerm(months);
};

/**
 * Label for a term KEY, which may carry a rate-type variant: "24" -> "Year 2",
 * "34_stepped" -> "Year 3 (Stepped)", "46_smooth" -> "Year 4 (Smooth)".
 * A key with no usable numeric prefix is returned untouched rather than becoming "Year NaN".
 */
export const formatTermKeyLabel = (
  termKey: string | number,
  mapping?: TermYearMapping | null,
): string => {
  const raw = String(termKey);
  const parts = raw.split('_');
  const months = parseInt(parts[0], 10);
  if (isNaN(months)) return raw;

  const suffix = parts.slice(1).join('_');
  const base = formatTermLabel(months, mapping);
  if (suffix === 'stepped') return `${base} (Stepped)`;
  if (suffix === 'smooth') return `${base} (Smooth)`;
  return base;
};

/**
 * The reviewer's own term -> year assignment, keyed by month count or full term key.
 *
 * Stored on the offer as `multi_year_rate_data.term_year_labels`, a JSONB field, so this needs
 * no migration. Absent on every existing offer, and absence means "fall back to the derived
 * number" — so adding this moves nothing that has already been generated or sent.
 *
 * `{ "16": 2 }` and `{ "16_stepped": 2 }` mean the same thing: a month count identifies a
 * term, and a "48"/"48_stepped" pair is one term offered two ways, sharing one year.
 */
export type TermYearMapping = Record<string, number | string>;

/** A mapping as month-count -> year, ignoring anything unusable. */
export const normaliseTermYearMapping = (
  mapping?: TermYearMapping | null,
): Map<number, number> => {
  const out = new Map<number, number>();
  if (!mapping || typeof mapping !== 'object') return out;

  for (const [key, value] of Object.entries(mapping)) {
    const months = parseInt(String(key).split('_')[0], 10);
    const year = typeof value === 'number' ? value : parseInt(String(value), 10);
    if (!isFinite(months) || months <= 0) continue;
    if (!Number.isInteger(year) || year < 1) continue;
    out.set(months, year);
  }
  return out;
};

/**
 * Does this term need the reviewer to say which year it is?
 *
 * A term that is a whole number of years answers itself — 12 -> Year 1, 48 -> Year 4,
 * 60 -> Year 5, 72 -> Year 6 — and asking would be busywork on the ~1,760 prod offers that
 * quote nothing else.
 *
 * Everything else is genuinely ambiguous and no rule settles it: WPG SYDNEY's 16-month term
 * must read Year 1 while GORKHA's 16-month term must read Year 2, from identical month counts.
 * Those are the ones to ask about.
 */
export const termNeedsYearMapping = (months: number | string): boolean => {
  const n = typeof months === 'number' ? months : parseInt(String(months).split('_')[0], 10);
  if (!isFinite(n) || n <= 0) return false;
  return n % 12 !== 0;
};

/**
 * Term keys still waiting for a year assignment.
 *
 * Used to flag an offer in review and to hold back tender generation: a half-mapped offer that
 * generates anyway produces a document disagreeing with the screen, which is the failure this
 * whole mechanism exists to stop.
 */
export const unmappedTermKeys = (
  termKeys: Array<string | number>,
  mapping?: TermYearMapping | null,
): string[] => {
  const mapped = normaliseTermYearMapping(mapping);
  return termKeys
    .map(k => String(k))
    .filter(key => {
      const months = parseInt(key.split('_')[0], 10);
      if (!isFinite(months) || months <= 0) return false;
      if (!termNeedsYearMapping(months)) return false;
      return !mapped.has(months);
    });
};

/**
 * Year numbers for a whole SERIES of terms from one offer, resolving collisions upward.
 *
 * ⚠️ A LABEL CANNOT BE DECIDED FROM ONE TERM'S MONTH COUNT ALONE.
 *
 * yearsForTerm snaps to the nearest bucket, which is right in isolation but can hand two terms
 * of the same offer the same year. EnergyAustralia quotes five options — 12 / 24 / 36 / 48 / 53
 * months, the last running to a calendar year end (02/08/2026 – 31/12/2030). 53 is nearer 48
 * than 60, so the fifth option was labelled "Year 4" beside the real 48-month one: two "Year 4"
 * tabs, and the offer's fifth-year rates filed under its fourth.
 *
 * No pure function of months can fix it. AGL's terms need 16 -> Year 1 (its "16 Months 12 Days"
 * first option), while this one needs 53 -> Year 5; any monotone months-to-year map that gives
 * the first breaks the second. The sibling terms are the missing information: an offer quoting
 * five options is quoting one per year.
 *
 * So the snap stands, and a term that collides with a shorter one is pushed to the next free
 * year. That leaves every non-colliding case exactly as before — an offer quoting only 24 and
 * 48 months still reads Year 2 and Year 4, which naive positional numbering would have called
 * Year 1 and Year 2.
 *
 * ⚠️ AND NO RULE SETTLES EVERY OFFER, which is why `mapping` overrides all of it.
 *
 * WPG SYDNEY's AGL pack quotes 4 / 16 / 28 / 40 / 52 months and its 16-month term must read
 * Year 1. GORKHA's Engie quote has the IDENTICAL month counts — its five terms end at five
 * consecutive period ends, 31/12/2026 through 31/12/2030 — and its 16-month term must read
 * Year 2. Same input, opposite correct answers, so the reviewer decides and the rules below
 * only supply a starting point for the terms they have not spoken about.
 *
 * @param months  - The series' month counts, in any order
 * @param mapping - The reviewer's assignment. A term listed here takes that year verbatim;
 *                  the rest are derived and flow around it.
 * @returns A month-count -> year-number map covering every input
 */
export const yearsForTermSeries = (
    months: Array<number | string>,
    mapping?: TermYearMapping | null,
): Map<number, number> => {
    const parsed = months
        .map(m => (typeof m === 'number' ? m : parseInt(String(m).split('_')[0], 10)))
        .filter(m => isFinite(m) && m > 0);
    const ascending = [...new Set(parsed)].sort((a, b) => a - b);

    // No mapping argument means "use the tender's", the same fallback termYearOf applies.
    // Without this the series functions ignored the assignment entirely, so Compare & Select
    // and the offer tabs — which label a whole series at once and have no mapping to hand —
    // kept showing the derived numbers while formatTermLabel callers showed the assigned ones.
    // A caller that DOES pass one wins outright, including an empty {} meaning "derived only".
    const assigned = (mapping !== undefined && mapping !== null)
        ? normaliseTermYearMapping(mapping)
        : getActiveTermYearMapping();

    const result = new Map<number, number>();
    let lastYear = 0;
    for (const m of ascending) {
        // The reviewer's own number wins outright.
        //
        // ⚠️ It must NOT advance lastYear. That floor exists to keep two DERIVED terms apart;
        // letting an assignment raise it made every later term jump: assigning the 4-month term
        // to Year 5 dragged 16/28/40/52 to Years 6/7/8/9, when only the term actually changed
        // should move. The `claimed` guard below is what keeps a derived term off an assigned
        // year, and it does so without shifting anything that was not in the way.
        const chosen = assigned.get(m);
        if (chosen !== undefined) {
            result.set(m, chosen);
            continue;
        }
        // A term shorter than a year is a STUB, not a contract year of its own, and must not
        // push the real terms up.
        //
        // AGL's offer packs open with a short pricing offer running to the first calendar year
        // end, then add twelve months per offer: WPG SYDNEY quotes 4 / 16 / 28 / 40 / 52
        // months. Letting the 4-month stub claim Year 1 bumped every real term — 16 read
        // "Year 2" where the client's model reads Year 1, and 52 read "Year 5" against their
        // Year 4. 203 offers across 83 tenders carry this shape (6/18/30/42/54, 7/19/31/43/55,
        // 5/17/29/41/53, 3/15/27/39/51 …), every one of them shifted by a single stub.
        //
        // So the stub takes its own rounded year — Year 1, the same as the 16-month term, which
        // is correct: both round to one year, and the reviewer deletes whichever they don't
        // want. It simply claims nothing, leaving the collision rule below to do its real job
        // of separating two genuine terms (EnergyAustralia's 48 and 53, which must not share).
        if (m < 12) {
            result.set(m, yearsForTerm(m));
            continue;
        }
        // Never below the shorter DERIVED term's year, and never equal to it. That floor is
        // between derived terms only — an assignment never displaces one.
        //
        // ⚠️ ASSIGNING ONE TERM MUST MOVE ONLY THAT TERM. Derived terms used to also dodge any
        // year the reviewer had claimed, which cascaded: on 4/16/28/40, setting 16 to Year 2
        // pushed 28 to Year 3 and then 40 to Year 4, so one edit silently renumbered three
        // terms. Two terms CAN now hold the same year, and that is deliberate — the clash is
        // reported by duplicateTermYears and blocks Approve, which is where the reviewer
        // decides what the other term should be. Nothing renumbers itself behind them.
        const year = Math.max(yearsForTerm(m), lastYear + 1);
        result.set(m, year);
        lastYear = year;
    }
    return result;
};

/**
 * Labels for a series of term KEYS, collision-free. Variant suffixes are preserved, and keys
 * sharing a month count (a "48" / "48_stepped" pair) share a year, as they should.
 *
 * @see yearsForTermSeries for why the series, not the single term, decides the number
 */
export const formatTermSeriesLabels = (
    termKeys: Array<string | number>,
    mapping?: TermYearMapping | null,
): Map<string, string> => {
    const yearByMonths = yearsForTermSeries(termKeys.map(k => String(k)), mapping);
    const out = new Map<string, string>();
    for (const key of termKeys) {
        const raw = String(key);
        const parts = raw.split('_');
        const months = parseInt(parts[0], 10);
        if (isNaN(months)) {
            out.set(raw, raw);
            continue;
        }
        const year = yearByMonths.get(months) ?? yearsForTerm(months);
        const suffix = parts.slice(1).join('_');
        const base = `Year ${year}`;
        out.set(raw,
            suffix === 'stepped' ? `${base} (Stepped)`
                : suffix === 'smooth' ? `${base} (Smooth)`
                    : base);
    }
    return out;
};

/**
 * Every year a term may be assigned to: Year 1 through Year N, where N is how many buckets are
 * configured (12/24/36/48/60/72 -> six).
 *
 * Deliberately NOT filtered to the free years. The reviewer needs to be able to put two terms
 * on the same year — that is how they discover the offer is wrong, or park one while they move
 * the other — so the picker offers everything and the CONFLICT is what blocks approval
 * (duplicateTermYears below). A picker that silently hides the year you were reaching for is
 * worse than one that lets you pick it and then tells you why it cannot be saved.
 */
export const termYearChoices = (): number[] =>
    Array.from({ length: Math.max(1, termBuckets.length) }, (_, i) => i + 1);

/**
 * Years that more than one of this offer's terms has been assigned to.
 *
 * Two terms sharing a year cannot both be right: the review screen would show two identically
 * named tabs and the generated document two sections with one heading. So an offer in this
 * state is not approvable, and this is what the Approve button checks.
 *
 * Keyed by month count, so a "48"/"48_stepped" pair — one term offered two ways — is one term
 * and never reported as a conflict with itself.
 *
 * Returns the offending year numbers, ascending; empty when the offer is clean.
 */
export const duplicateTermYears = (
    termKeys: Array<string | number>,
    mapping?: TermYearMapping | null,
): number[] => {
    // An offer nobody has mapped is held to today's rules, where a sub-year stub deliberately
    // SHARES Year 1 with the first real term (WPG SYDNEY's 4 and 16 months). Enforcing
    // uniqueness there would make every existing unconventional offer un-approvable. Once the
    // reviewer assigns even one term, the offer has to come out conflict-free.
    if (normaliseTermYearMapping(mapping).size === 0) return [];

    const yearByMonths = yearsForTermSeries(termKeys.map(k => String(k)), mapping);
    const seen = new Map<number, number>();
    for (const year of yearByMonths.values()) {
        seen.set(year, (seen.get(year) ?? 0) + 1);
    }
    return [...seen.entries()]
        .filter(([, count]) => count > 1)
        .map(([year]) => year)
        .sort((a, b) => a - b);
};
/**
 * Hidden stamp the proposal generator writes alongside a term heading, and searches BACKWARD
 * for to decide which term a given cost placeholder belongs to.
 *
 * ⚠️ THIS IS A ROUND TRIP. scopingAi.ts writes termHeading() into templateStr (STEP 2), then
 * findTermForPosition (STEP 4) does a lastIndexOf for termSearchToken() to pair each {rN_*}
 * cost placeholder with its term. If the written and searched forms ever diverge, the search
 * silently returns null, every cost placeholder falls through to the virtual-offer ranking,
 * and the tender generates with plausible figures on the WRONG pages — no error, no crash.
 *
 * The search deliberately keys on this OPAQUE stamp rather than on the visible label, because
 * a visible label is a substring hazard: searching for "Year 1" would also match inside
 * "Rate Year 1" (the rate-escalation heading) and mis-pair the figure with the 12-month term.
 *
 * ⚠️ THE STAMP MUST USE THE SAME YEAR THE HEADING SHOWS.
 *
 * It used to derive its number from yearsForTerm alone, ignoring the reviewer's term -> year
 * assignment that formatTermLabel right below it honours. Two terms the reviewer deliberately
 * separated then collided on one stamp, and lastIndexOf paired the later page's costs to the
 * earlier term.
 *
 * Prod tender bd3f75f1 (WERRIBEE RACING CLUB, terms 4/16/28/40, mapping {16:2, 28:3, 40:4}):
 * yearsForTerm snaps both 4 and 16 to the 12-month bucket, so both stamped __TERM_1__ while
 * their headings read "Year 1" and "Year 2". The Year 2 page therefore printed the 4-month
 * figures, and the two retailers with no 4-month term (Flo, Next Business) dropped off it
 * entirely.
 *
 * Passing the mapping is not a behaviour change for anything unmapped: termYearOf falls back
 * to yearsForTerm whenever the term has no assignment, so every tender without a reviewer
 * mapping — and every standard 12/24/36/48 term, which maps to the same year either way —
 * produces the byte-identical stamp it does today.
 *
 * Same technique — and same lifecycle — as the __SITE_INDEX_N__ stamps used for multi-site
 * block detection: written early, searched during placeholder resolution, stripped before the
 * document is returned (see TERM_STAMP_PATTERN).
 */
export const termSearchToken = (months: number, mapping?: TermYearMapping | null): string =>
  `__TERM_${termYearOf(months, mapping)}__`;

/** Strips every term stamp. Must run before the generated document is returned. */
export const TERM_STAMP_PATTERN = /__TERM_\d+__/g;

/**
 * What actually replaces `{contract_term}`: the invisible pairing stamp followed by the
 * human-readable heading.
 *
 * The visible part reads as a LENGTH — "1 Year", so a template writing "{contract_term}
 * Contract" produces "1 Year Contract" rather than "Year 1 Contract". Same wording as the
 * best-offer cards, so one document never names the same thing two ways.
 *
 * ⚠️ ONLY THE VISIBLE PART CHANGES. Pairing keys on the stamp, never on this text — see the
 * note on termSearchToken — so the number, the stamp and every cost placeholder it pairs are
 * untouched. The label is also still built from termYearOf, so an unconventional 16-month term
 * assigned to Year 2 keeps reading "2 Year" and stays on the page its figures came from.
 */
export const termHeading = (months: number, mapping?: TermYearMapping | null): string =>
  `${termSearchToken(months, mapping)}${formatContractTermLabel(months, mapping)}`;

/**
 * True when two or more of the supplied terms would render the SAME visible label.
 *
 * requested_contract_terms is org-configurable (sys_config), not fixed at [12,24,36,48]. A set
 * like [10, 14] collapses to "Year 1" twice, which would show two identically-named tabs. Cost
 * pairing is NOT at risk (the stamp above stays unique), so this is a presentation warning
 * only — but it is worth surfacing rather than shipping a duplicate heading silently.
 */
export const termLabelsCollide = (terms: Array<number | string>): boolean => {
  const seen = new Set<string>();
  for (const t of terms) {
    const months = parseInt(String(t).split('_')[0], 10);
    if (isNaN(months)) continue;
    const label = formatTermLabel(months);
    if (seen.has(label)) return true;
    seen.add(label);
  }
  return false;
};

/**
 * Rate escalation year inside a term — "Year 1", "Year 2".
 *
 * Reads plain "Year N", as it did before the "Rate Year N" wording was introduced. That
 * prefix was added to keep a rate year visually distinct from a 12-month term also called
 * "Year 1"; the reviewer found it worse to read than the ambiguity was worth, so the original
 * wording stands. The ambiguity is cosmetic only — nothing matches on this string: the
 * proposal generator pairs cost placeholders to terms through the hidden __TERM_N__ stamp
 * (see termHeading below and scopingAi.ts STEP 4), never through the visible label.
 *
 * Accepts a `year_2` style key or a bare number.
 */
export const formatRateYearLabel = (year: string | number): string => {
  const n = typeof year === 'number' ? year : parseInt(String(year).replace(/\D/g, ''), 10);
  if (isNaN(n)) return String(year);
  return `Year ${n}`;
};

/** Inclusive rate-year span: "Year 1-2", or "Year 1" when first === last. */
export const formatRateYearRange = (first: number, last: number): string =>
  last > first ? `Year ${first}-${last}` : `Year ${first}`;

/**
 * Which contract year a term has been ASSIGNED to, from either place an assignment can live.
 *
 * ⚠️ NOT THE SAME AS THE LABEL ON A TAB, AND THAT IS THE WHOLE POINT.
 *
 * The review screen draws one tab per TERM when an offer quotes several, and one tab per YEAR
 * ROW when it quotes only one. In the first case a tab's label already comes from this mapping,
 * so reading the label back gave the right answer. In the second the label is the row's own year
 * — derived from its dates, blind to any assignment — so the "this term is Year N" control
 * displayed the row while writing the term. Every choice snapped back: picking Year 2 stored it,
 * the re-render recomputed the same row label, and nothing moved on screen.
 *
 * Surfaced after deleting a year from a two-year term. removeTermYear renumbers year_2 down to
 * year_1, which is correct, and the reviewer was then left with no way to say the term is still
 * Year 2.
 *
 * The offer's own assignment wins over the tender-wide one — the order formatTermSeriesLabels
 * already uses — because a reviewer editing this offer is answering for this offer.
 *
 * @param mapping  the offer's own assignment, as stored on `term_year_labels`
 * @param months   the term's month count; "48" and "48_stepped" share one assignment
 * @returns the assigned year, or null when neither place has one and the caller should fall
 *          back to whatever the tab is labelled
 */
export const resolveAssignedTermYear = (
  mapping: TermYearMapping | null | undefined,
  months: number,
): number | null => {
  if (!Number.isFinite(months) || months <= 0) return null;
  return normaliseTermYearMapping(mapping).get(months)
    ?? getActiveTermYearMapping().get(months)
    ?? null;
};
